import {
  useEffect,
  useState,
} from "react";

import ClientAutocomplete from "./ClientAutocomplete";
import ReservationPaymentPanel from "./ReservationPaymentPanel";

import type {
  Client,
  ClientLevel,
} from "../../clientes/types";

import type {
  Booking,
  FormaPagamentoQuadra,
} from "../types";

import "./BookingModal.css";

type QuickClientInput = {
  nome: string;
  telefone: string;
  nivel: ClientLevel;
};

type BookingModalProps = {
  open: boolean;

  booking:
    | Booking
    | null;

  court: string;

  hour: number;

  cliente: string;

  selectedClient:
    | Client
    | null;

  clients: Client[];

  duration: number;

  editableValue: number;

  saving: boolean;

  error: string;

  canDelete: boolean;

  formatHour: (
    hour: number,
  ) => string;

  onClienteChange: (
    value: string,
  ) => void;

  onClientSelect: (
    client: Client,
  ) => void;

  onDurationChange: (
    duration: number,
  ) => void;

  onBookingValueChange: (
    value: number,
  ) => void;

  onQuickCreateClient: (
    input: QuickClientInput,
  ) => Promise<Client>;

  onClose: () => void;

  onSave: () => void;

  onDelete: () => void;

  /*
   * PAGAMENTO
   *
   * Opcionais por enquanto para que
   * possamos atualizar este arquivo
   * antes do Quadras.tsx.
   */
  canReceive?: boolean;

  canCorrectPayments?: boolean;

  receiving?: boolean;

  paymentError?: string;

  onReceive?: (
    formaPagamento:
      FormaPagamentoQuadra,
  ) => Promise<void>;

  onPaymentsChanged?: () => Promise<void> | void;
};

const LEVELS:
  ClientLevel[] = [
    "Iniciante",
    "D",
    "C",
    "B",
    "A",
  ];

const PAYMENT_OPTIONS: {
  value:
    FormaPagamentoQuadra;

  label: string;
}[] = [
  {
    value: "pix",
    label: "Pix",
  },
  {
    value: "dinheiro",
    label: "Dinheiro",
  },
  {
    value: "debito",
    label: "Débito",
  },
  {
    value: "credito",
    label: "Crédito",
  },
];

function formatBRL(
  value: number,
) {
  return new Intl.NumberFormat(
    "pt-BR",
    {
      style: "currency",
      currency: "BRL",
    },
  ).format(value);
}

function formatPaymentName(
  payment:
    FormaPagamentoQuadra,
) {
  switch (payment) {
    case "pix":
      return "Pix";

    case "dinheiro":
      return "Dinheiro";

    case "debito":
      return "Débito";

    case "credito":
      return "Crédito";

    default:
      return payment;
  }
}

function formatPaidAt(
  value: string,
) {
  return new Intl.DateTimeFormat(
    "pt-BR",
    {
      dateStyle: "short",
      timeStyle: "short",
    },
  ).format(
    new Date(value),
  );
}

export default function BookingModal({
  open,
  booking,
  court,
  hour,
  cliente,
  selectedClient,
  clients,
  duration,
  editableValue,
  saving,
  error,
  canDelete,
  formatHour,
  onClienteChange,
  onClientSelect,
  onDurationChange,
  onBookingValueChange,
  onQuickCreateClient,
  onClose,
  onSave,
  onDelete,

  canReceive = false,

  canCorrectPayments = false,

  receiving = false,

  paymentError = "",

  onReceive,
  onPaymentsChanged,
}: BookingModalProps) {
  const [
    showQuickCreate,
    setShowQuickCreate,
  ] =
    useState(false);

  const [
    quickName,
    setQuickName,
  ] =
    useState("");

  const [
    quickPhone,
    setQuickPhone,
  ] =
    useState("");

  const [
    quickLevel,
    setQuickLevel,
  ] =
    useState<ClientLevel>(
      "Iniciante",
    );

  const [
    quickSaving,
    setQuickSaving,
  ] =
    useState(false);

  const [
    quickError,
    setQuickError,
  ] =
    useState("");

  const [
    showPayment,
    setShowPayment,
  ] =
    useState(false);

  const [
    paymentMethod,
    setPaymentMethod,
  ] =
    useState<FormaPagamentoQuadra>(
      "pix",
    );

  useEffect(() => {
    if (!open) {
      setShowQuickCreate(
        false,
      );

      setQuickName("");

      setQuickPhone("");

      setQuickLevel(
        "Iniciante",
      );

      setQuickError("");

      setShowPayment(
        false,
      );

      setPaymentMethod(
        "pix",
      );
    }
  }, [open]);

  if (!open) {
    return null;
  }

  async function handleQuickCreate() {
    if (
      !quickName.trim()
    ) {
      setQuickError(
        "Informe o nome do cliente.",
      );

      return;
    }

    try {
      setQuickSaving(true);

      setQuickError("");

      const created =
        await onQuickCreateClient({
          nome:
            quickName.trim(),

          telefone:
            quickPhone.trim(),

          nivel:
            quickLevel,
        });

      onClientSelect(
        created,
      );

      setShowQuickCreate(
        false,
      );

      setQuickName("");

      setQuickPhone("");

      setQuickLevel(
        "Iniciante",
      );
    } catch (err) {
      setQuickError(
        err instanceof Error
          ? err.message
          : "Não foi possível cadastrar o cliente.",
      );
    } finally {
      setQuickSaving(
        false,
      );
    }
  }

  async function handlePayment() {
    if (!onReceive) {
      return;
    }

    await onReceive(
      paymentMethod,
    );
  }

  const bookingValue =
    Number(
      booking?.price ??
        0,
    );

  const isPaid =
    Boolean(
      booking?.pago,
    );

  return (
    <div
      className="booking-modal-backdrop"
      onClick={onClose}
    >
      <div
        className="booking-modal"
        onClick={(
          event,
        ) =>
          event.stopPropagation()
        }
      >
        <div className="booking-modal-header">
          <div>
            <span className="quadras-eyebrow">
              {booking
                ? "RESERVA"
                : "NOVA RESERVA"}
            </span>

            <h3>
              {court}
            </h3>

            <p>
              {formatHour(
                hour,
              )}
            </p>
          </div>

          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            disabled={
              saving ||
              quickSaving ||
              receiving
            }
          >
            ×
          </button>
        </div>

        {!showQuickCreate ? (
          <>
            <label className="booking-field">
              Cliente

              <ClientAutocomplete
                clients={
                  clients
                }
                value={
                  cliente
                }
                selectedClient={
                  selectedClient
                }
                onChange={
                  onClienteChange
                }
                onSelect={
                  onClientSelect
                }
                onRequestCreate={(
                  name,
                ) => {
                  setQuickName(
                    name,
                  );

                  setQuickPhone(
                    "",
                  );

                  setQuickLevel(
                    "Iniciante",
                  );

                  setQuickError(
                    "",
                  );

                  setShowQuickCreate(
                    true,
                  );
                }}
              />
            </label>

            <label className="booking-field">
              Duração

              <select
                value={
                  duration
                }
                onChange={(
                  event,
                ) =>
                  onDurationChange(
                    Number(
                      event.target
                        .value,
                    ),
                  )
                }
                disabled={
                  isPaid
                }
              >
                <option value={0.5}>
                  30 minutos
                </option>

                <option value={1}>
                  1 hora
                </option>

                <option value={1.5}>
                  1h30
                </option>

                <option value={2}>
                  2 horas
                </option>

                <option value={2.5}>
                  2h30
                </option>

                <option value={3}>
                  3 horas
                </option>
              </select>
            </label>

            {booking &&
              !isPaid && (
                <label className="booking-field">
                  Valor (R$)

                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={
                      editableValue
                    }
                    onChange={(
                      event,
                    ) =>
                      onBookingValueChange(
                        Number(
                          event.target
                            .value,
                        ),
                      )
                    }
                    disabled={
                      saving ||
                      receiving
                    }
                  />
                </label>
              )}

            {booking && (
              <div className="booking-payment-summary">
                <div>
                  <span>
                    Valor da reserva
                  </span>

                  <strong>
                    {formatBRL(
                      bookingValue,
                    )}
                  </strong>
                </div>

                <div>
                  <span>
                    Status
                  </span>

                  <strong
                    className={
                      isPaid
                        ? "booking-status-paid"
                        : "booking-status-pending"
                    }
                  >
                    {isPaid
                      ? "Pago"
                      : "Pendente"}
                  </strong>
                </div>
              </div>
            )}

            {booking &&
              isPaid && (
                <div className="booking-paid-box">
                  <div className="booking-paid-icon">
                    ✓
                  </div>

                  <div>
                    <strong>
                      Pagamento confirmado
                    </strong>

                    <span>
                      {booking.forma_pagamento
                        ? formatPaymentName(
                            booking.forma_pagamento,
                          )
                        : "Forma de pagamento não informada"}

                      {booking.pago_em
                        ? ` · ${formatPaidAt(
                            booking.pago_em,
                          )}`
                        : ""}
                    </span>
                  </div>
                </div>
              )}

            {booking &&
              canReceive &&
              onReceive &&
              onPaymentsChanged && (
                <ReservationPaymentPanel
                  booking={booking}
                  disabled={saving || receiving}
                  canCorrect={canCorrectPayments}
                  onIntegral={onReceive}
                  onChanged={onPaymentsChanged}
                />
              )}

            {booking &&
              !isPaid &&
              canReceive &&
              onReceive &&
              !onPaymentsChanged && (
                <div className="booking-payment-area">
                  {!showPayment ? (
                    <button
                      type="button"
                      className="receive-button"
                      onClick={() =>
                        setShowPayment(
                          true,
                        )
                      }
                      disabled={
                        saving ||
                        receiving
                      }
                    >
                      Receber{" "}
                      {formatBRL(
                        bookingValue,
                      )}
                    </button>
                  ) : (
                    <>
                      <div className="booking-payment-header">
                        <div>
                          <span className="quadras-eyebrow">
                            PAGAMENTO
                          </span>

                          <h4>
                            Receber reserva
                          </h4>
                        </div>

                        <strong>
                          {formatBRL(
                            bookingValue,
                          )}
                        </strong>
                      </div>

                      <div className="booking-payment-methods">
                        {PAYMENT_OPTIONS.map(
                          (
                            option,
                          ) => (
                            <button
                              key={
                                option.value
                              }
                              type="button"
                              className={
                                paymentMethod ===
                                option.value
                                  ? "payment-method active"
                                  : "payment-method"
                              }
                              onClick={() =>
                                setPaymentMethod(
                                  option.value,
                                )
                              }
                              disabled={
                                receiving
                              }
                            >
                              {
                                option.label
                              }
                            </button>
                          ),
                        )}
                      </div>

                      {paymentError && (
                        <div className="booking-form-error">
                          {
                            paymentError
                          }
                        </div>
                      )}

                      <div className="booking-payment-actions">
                        <button
                          type="button"
                          className="cancel-button"
                          onClick={() =>
                            setShowPayment(
                              false,
                            )
                          }
                          disabled={
                            receiving
                          }
                        >
                          Voltar
                        </button>

                        <button
                          type="button"
                          className="confirm-payment-button"
                          onClick={
                            handlePayment
                          }
                          disabled={
                            receiving
                          }
                        >
                          {receiving
                            ? "Recebendo..."
                            : `Confirmar ${formatPaymentName(
                                paymentMethod,
                              )}`}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

            {error && (
              <div className="booking-form-error">
                {error}
              </div>
            )}

            {/*
             * Reserva paga não deve ter duração
             * nem dados financeiros alterados
             * por este fluxo.
             */}
            {!isPaid && (
              <div className="booking-modal-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={
                    onClose
                  }
                  disabled={
                    saving ||
                    receiving
                  }
                >
                  {booking
                    ? "Fechar"
                    : "Cancelar"}
                </button>

                <button
                  type="button"
                  className="confirm-button"
                  onClick={
                    onSave
                  }
                  disabled={
                    saving ||
                    receiving ||
                    !cliente.trim()
                  }
                >
                  {saving
                    ? "Salvando..."
                    : booking
                      ? "Salvar alterações"
                      : "Confirmar reserva"}
                </button>
              </div>
            )}

            {booking &&
              isPaid && (
                <button
                  type="button"
                  className="booking-paid-close"
                  onClick={
                    onClose
                  }
                >
                  Fechar
                </button>
              )}

            {booking &&
              canDelete &&
              !isPaid && (
                <button
                  type="button"
                  className="danger-button"
                  onClick={
                    onDelete
                  }
                  disabled={
                    saving ||
                    receiving
                  }
                >
                  Cancelar reserva
                </button>
              )}
          </>
        ) : (
          <div className="quick-client-form">
            <div className="quick-client-title">
              <span className="quadras-eyebrow">
                CADASTRO RÁPIDO
              </span>

              <h4>
                Novo cliente
              </h4>

              <p>
                O cliente será
                cadastrado e
                selecionado
                automaticamente
                na reserva.
              </p>
            </div>

            <label className="booking-field">
              Nome

              <input
                autoFocus
                value={
                  quickName
                }
                onChange={(
                  event,
                ) =>
                  setQuickName(
                    event.target
                      .value,
                  )
                }
              />
            </label>

            <label className="booking-field">
              Telefone

              <input
                value={
                  quickPhone
                }
                onChange={(
                  event,
                ) =>
                  setQuickPhone(
                    event.target
                      .value,
                  )
                }
                placeholder="(99) 99999-9999"
              />
            </label>

            <label className="booking-field">
              Nível

              <select
                value={
                  quickLevel
                }
                onChange={(
                  event,
                ) =>
                  setQuickLevel(
                    event.target
                      .value as ClientLevel,
                  )
                }
              >
                {LEVELS.map(
                  (level) => (
                    <option
                      key={
                        level
                      }
                      value={
                        level
                      }
                    >
                      {level}
                    </option>
                  ),
                )}
              </select>
            </label>

            {quickError && (
              <div className="booking-form-error">
                {quickError}
              </div>
            )}

            <div className="booking-modal-actions">
              <button
                type="button"
                className="cancel-button"
                onClick={() =>
                  setShowQuickCreate(
                    false,
                  )
                }
                disabled={
                  quickSaving
                }
              >
                Voltar
              </button>

              <button
                type="button"
                className="confirm-button"
                onClick={
                  handleQuickCreate
                }
                disabled={
                  quickSaving ||
                  !quickName.trim()
                }
              >
                {quickSaving
                  ? "Cadastrando..."
                  : "Cadastrar e selecionar"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
